import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { ApplicationEnvironmentMolecule } from "@/app/env/store"
import { EvmSdkContainerMoleculeReatom } from "@/app/sdk/blockchains/evm-sdk-container-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { getRawAddress } from "@anaconda/core/blockchain/address"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { getThirdwebChain } from "@anaconda/core/vendors/thirdweb/chains"
import { computed } from "@reatom/core"
import { molecule } from "bunshi"
import { createThirdwebClient, getContract } from "thirdweb"
import { viemAdapter } from "thirdweb/adapters/viem"

export const ThirdwebMoleculeReatom = molecule((mol) => {
  const { connectedBlockchain } = mol(WalletConnectMoleculeReatom)
  const { evmBlockchainsList } = mol(BlockchainsMoleculeReatom)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)

  const { thirdwebClientId, ipfsGateway } = mol(ApplicationEnvironmentMolecule)

  const client = createThirdwebClient({
    clientId: thirdwebClientId,
    config: {
      storage: {
        gatewayUrl: ipfsGateway,
      },
    },
  })

  // Get wallet client from SDK container
  const walletClient = computed(async () => {
    const container = sdkContainerAtom()
    if (!container) return null

    try {
      const { ReatomTokens } = await import(
        "@rarible_int/anaconda_sdk/ethereum"
      )
      const walletClientAtom = container.resolve(
        ReatomTokens.WALLET_CLIENT_ATOM,
      )
      return walletClientAtom()
    } catch (error) {
      console.error("Failed to get wallet client from SDK:", error)
      return null
    }
  }, "thirdwebWalletClient")

  // Create thirdweb account from wallet client
  const thirdwebAccount = computed(async () => {
    const walletClientValue = await walletClient()

    if (!walletClientValue) {
      throw new Error(
        "Thirdweb account creation failed: wallet client is not defined",
      )
    }

    // We can not safely update thirdweb package at the moment.
    const thirdwebWallet = viemAdapter.wallet.fromViem({
      // @ts-expect-error different version of viem in the app and thirdweb package leads to this error.
      walletClient: walletClientValue,
    })

    return thirdwebWallet.connect({ client })
  }, "thirdwebAccount")

  // Computed: thirdweb chain
  const thirdwebChain = computed(async () => {
    const blockchainData = await connectedBlockchain()
    const blockchainConfigs = evmBlockchainsList()
    if (!blockchainData.data) return null

    const blockchainConfig = blockchainConfigs.find(
      (b) => b.blockchain === blockchainData.data,
    )

    return blockchainConfig ? getThirdwebChain(blockchainConfig) : null
  }, "thirdwebChain")

  // Factory: contract instance
  const reatomContract = (address: CollectionId, blockchain: Blockchain) =>
    computed(() => {
      const blockchainConfigs = evmBlockchainsList()

      const blockchainConfig = blockchainConfigs.find(
        (b) => b.blockchain === blockchain,
      )

      if (!blockchainConfig) {
        throw new Error(
          "Thirdweb contract creation failed: blockchain config is not defined",
        )
      }

      return getContract({
        client,
        chain: getThirdwebChain(blockchainConfig),
        address: getRawAddress(address),
      })
    }, `thirdwebContract_${blockchain}_${address}`)

  return {
    client,
    reatomContract,
    thirdwebAccount,
    thirdwebChain,
    walletClient,
  }
})
